<template>
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <div
        :style="{
            height: height,
            'max-height': maxHeight
        }"
        @mousedown="onMouseDown"
        @mouseup="onMouseUp"
    >
        <v-data-table
            :ref="id"
            v-model="selected"
            :class="tableClassList"
            :headers="visibleHeaders"
            :items="rows"
            :loading="!showLacuna && (loading || isLoading)"
            :hide-default-footer="true"
            :page="options.page"
            :items-per-page="options.itemsPerPage"
            :server-items-length="itemsLength"
            :sort-by="options.sortBy"
            :sort-desc="options.sortDesc"
            :show-select="!!selectable"
            :single-select="selectable === 1"
            :show-expand="expandable"
            :fixed-header="fixedHeader"
            :dense="dense"
            :mobile-breakpoint="isMobile || display === 'cards' ? 9999 : 0"
            :hide-default-header="hideHeaderBar || !showDefaultHeader"
            disable-sort
            :height="height"
            :style="{
                'max-height': maxHeight
            }"
            @update:options="updateOptions"
            @click:row="onClickRow"
            @item-expanded="$emit('expanded:row', $event)"
            @toggle-select-all="selectAllToggle"
        >
            <template v-if="!showLacuna" #loading>
                <app-table-loading />
            </template>

            <template #no-data>
                <app-table-no-data :no-data-message="noDataMessage" />
            </template>

            <template v-if="!hideTop && !showLacuna" #top="attr">
                <app-table-top
                    :attr="attr"
                    :exportable="exportable"
                    :headers="headers"
                    :export-headers="exportHeaders"
                    :active-header-keys="activeHeaderKeys"
                    :request-items="requestItems"
                    :filter-panel-id="filterPanelId"
                    :filter-request-items="filterRequestItems"
                    :filters="params.filters"
                    :display="display"
                    :display-formats="formats"
                    :lock-display="lockDisplay"
                    :items="items"
                    :hide-column-visibility="hideColumnVisibility"
                    :hide-filters="hideFilters"
                    :hide-actions="hideActions"
                    :cast="cast"
                    :loading="loading || isLoading"
                    @header-visibility="v => (activeHeaderKeys = v)"
                    @display="v => (display = v)"
                    @apply-filters="onApplyFilters"
                    @clear-filters="onClearFilters"
                    @loaded-filters="filtersLoaded = true"
                >
                    <template v-if="findSlot('heading')" #heading>
                        <slot name="heading" :error-messages="errorMessages" />
                    </template>
                    <template v-else-if="errorMessages.length" #heading>
                        <p v-for="(errorMessage, index) of errorMessages" :key="index" class="error--text mb-1">
                            {{ errorMessage }}
                        </p>
                    </template>
                    <template v-if="findSlot('before-actions')" #before-actions>
                        <slot name="before-actions" />
                    </template>
                    <template v-if="findSlot('actions')" #actions>
                        <slot name="actions" />
                    </template>
                </app-table-top>

                <template v-if="findSlot('after-top')">
                    <slot name="after-top" />
                </template>
            </template>

            <template v-if="showLacuna" #body>
                <tbody class="app-table__lacuna">
                    <tr>
                        <td :colspan="visibleHeaders.length + 1">
                            <slot name="lacuna" />
                        </td>
                    </tr>
                </tbody>
            </template>

            <template v-else-if="display === 'kanban'" #body>
                <slot
                    v-if="findSlot('kanban')"
                    name="kanban"
                    v-bind="{
                        group: kanbanGroup,
                        columns: kanbanColumns,
                        data: kanbanData,
                        loading: loading || isLoading
                    }"
                />
                <template v-else>
                    <v-row class="ma-0" style="height: 100%; flex-wrap: nowrap">
                        <kanban-column
                            v-for="(kanbanRows, kanbanColumnIndex) in kanbanData"
                            :key="`kanban-${kanbanColumnIndex}`"
                            v-test-id="`kanban-column-${kanbanColumnIndex}`"
                            :loading="loading || isLoading"
                        >
                            <template v-if="findSlot(`kanban.${kanbanColumnIndex}.header`)" #header>
                                <slot
                                    :name="`kanban.${kanbanColumnIndex}.header`"
                                    v-bind="{
                                        group: kanbanGroup,
                                        columns: kanbanColumns,
                                        column: kanbanColumnIndex,
                                        data: kanbanRows,
                                        loading: loading || isLoading
                                    }"
                                />
                            </template>
                            <template v-else #heading>{{ $t(kanbanColumnIndex) }}</template>

                            <template v-if="findSlot(`kanban.${kanbanColumnIndex}.boards`)" #boards>
                                <slot
                                    :name="`kanban.${kanbanColumnIndex}.boards`"
                                    v-bind="{
                                        group: kanbanGroup,
                                        columns: kanbanColumns,
                                        column: kanbanColumnIndex,
                                        data: kanbanRows,
                                        loading: loading || isLoading
                                    }"
                                />
                                <slot
                                    :name="`kanban.${kanbanColumnIndex}.boards.after`"
                                    v-bind="{
                                        group: kanbanGroup,
                                        columns: kanbanColumns,
                                        column: kanbanColumnIndex,
                                        data: kanbanRows,
                                        loading: loading || isLoading
                                    }"
                                />
                            </template>
                            <template v-else #boards>
                                <kanban-board
                                    v-for="(kanbanRow, kanbanRowIndex) in kanbanRows"
                                    :key="`kanban-${kanbanColumnIndex}-${kanbanRowIndex}`"
                                >
                                    {{ kanbanRow }}
                                </kanban-board>
                            </template>
                        </kanban-column>
                    </v-row>
                </template>
            </template>

            <template #footer>
                <slot v-if="findSlot('footer')" name="footer" />
                <app-table-footer
                    v-else-if="showFooter"
                    :items-per-page="options.itemsPerPage"
                    :page="options.page"
                    :total="response.metadata.total"
                    :disabled="loading || isLoading"
                    :loading="loading || isLoading"
                    :lazy-load="lazyLoad"
                    @update-options="updateOptions"
                />
            </template>

            <template v-if="!hideHeaderBar && hideDefaultHeader" #header="data">
                <app-table-header
                    :headers="data.props.headers"
                    :options="data.props.options"
                    :mobile="data.props.mobile"
                    :show-group-by="data.props.showGroupBy"
                    :some-items="someItemsSelected"
                    :every-item="everyItemSelected"
                    :single-select="data.props.singleSelect"
                    :selectable="selectable"
                    :disable-sort="!sortable"
                    :loading="loading || isLoading"
                    :show-quick-filters="showQuickFilters && filtersLoaded"
                    :filter-panel-id="filterPanelId"
                    :filters="params.filters"
                    :value="value"
                    v-on="data.on"
                    @sort="onSort"
                    @apply-filters="onApplyFilters"
                    @clear-filters="onClearFilters"
                />
            </template>

            <template v-if="!hideProgress" #progress>
                <v-progress-linear
                    v-if="loading || isLoading"
                    indeterminate
                    color="primaryBackground"
                    height="3px"
                    style="margin-top: -3px"
                />
            </template>

            <template #[`header.data-table-select`]="attr">
                <p>select</p>
                <app-table-header-data-table-select :attr="attr" />
            </template>

            <template #[`item.data-table-select`]="attr">
                <app-table-item-data-table-select
                    :v-model="selected"
                    :attr="attr"
                    :selectable="selectable"
                    :max="maxSelectable"
                />
            </template>

            <template #[`item.data-table-expand`]="{ expand, isExpanded }">
                <span class="app-table-expand-icon">
                    <v-icon
                        small
                        dense
                        :title="isExpanded ? $t('collapse') : $t('expand')"
                        :class="isExpanded ? 'fa-rotate-90' : false"
                        @click="expand(!isExpanded)"
                    >
                        {{ getIcon('expand') }}
                    </v-icon>
                </span>
            </template>

            <template #expanded-item="data">
                <td :colspan="expandedItemColspan" class="app-table__expanded-item">
                    <slot v-if="data" name="expanded-item" v-bind="{ ...data, display, width: expandedItemWidth }" />
                </td>
            </template>

            <template v-if="showCardSlot" #item="data">
                <slot
                    name="card"
                    v-bind="{
                        ...data,
                        getHeader: h => headers.find(v => v.value === h),
                        getValue: h =>
                            getTableRowValue({
                                header: headers.find(v => v.value === h),
                                item: data.item,
                                value: data.item[`${h}`]
                            })
                    }"
                />
            </template>

            <template v-for="slot in itemSlots" #[slot]="data">
                <app-table-item-slot :key="slot" :value="data" :default-value="getTableRowFallback(data)">
                    <slot
                        :name="slot"
                        v-bind="{ ...data, display, value: getTableRowValue(data) }"
                        :class="
                            _.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList
                        "
                    />
                </app-table-item-slot>
            </template>

            <!--
            Format: control
        -->
            <template v-for="slotControl in getSlotsByDisplayFormat('control')" #[slotControl]="data">
                <slot
                    name="item.control"
                    v-bind="{ ...data }"
                    :class="_.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList"
                />
            </template>

            <!--
            Format: blank
        -->
            <template v-for="slotBlank in getSlotsByDisplayFormat('blank')" #[slotBlank]>
                <div
                    :key="slotBlank"
                    class="rounded"
                    style="height: 1rem; width: 5rem; background: rgba(0, 0, 0, 0.1)"
                />
            </template>

            <!--
            Format: string
        -->
            <template v-for="slotString in getSlotsByDisplayFormat('string')" #[slotString]="data">
                <app-table-item-slot
                    :key="slotString"
                    :value="data"
                    :show-default-value="isNil(data)"
                    :default-value="getTableRowFallback(data)"
                >
                    <display-string
                        :value="getTableRowValue(data)"
                        :options="data.header.formatOptions"
                        :class="
                            _.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList
                        "
                    />
                    <template #edit="{ saving }">
                        <edit-string
                            v-model="data.header.editable.values[data.item.id]"
                            :options="data.header.formatOptions"
                            :loading="saving"
                            @click.native.stop
                        />
                    </template>
                </app-table-item-slot>
            </template>

            <!--
            Format: number
        -->
            <template v-for="slotNumber in getSlotsByDisplayFormat('number')" #[slotNumber]="data">
                <app-table-item-slot
                    :key="slotNumber"
                    :value="data"
                    :show-default-value="isNil(data)"
                    :default-value="getTableRowFallback(data)"
                >
                    <display-number
                        :value="getTableRowValue(data)"
                        :options="data.header.formatOptions"
                        :class="
                            _.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList
                        "
                    />
                    <template #edit="{ saving }">
                        <edit-number
                            v-model="data.header.editable.values[data.item.id]"
                            :options="data.header.formatOptions"
                            :loading="saving"
                            @click.native.stop
                        />
                    </template>
                </app-table-item-slot>
            </template>

            <!--
            Format: money
        -->
            <template v-for="slotCurrency in getSlotsByDisplayFormat('money')" #[slotCurrency]="data">
                <app-table-item-slot
                    :key="slotCurrency"
                    :value="data"
                    :show-default-value="isNil(data)"
                    :default-value="getTableRowFallback(data)"
                >
                    <display-money
                        :value="getTableRowValue(data)"
                        :options="data.header.formatOptions"
                        :class="
                            _.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList
                        "
                    />
                    <template #edit="{ saving }">
                        <edit-money
                            v-model="data.header.editable.values[data.item.id]"
                            :options="data.header.formatOptions"
                            :loading="saving"
                            @click.native.stop
                        />
                    </template>
                </app-table-item-slot>
            </template>

            <!--
            Format: date
        -->
            <template v-for="slotDate in getSlotsByDisplayFormat('date')" #[slotDate]="data">
                <app-table-item-slot
                    :key="slotDate"
                    :value="data"
                    :show-default-value="isNil(data)"
                    :default-value="getTableRowFallback(data)"
                >
                    <display-date
                        :value="getTableRowValue(data)"
                        :options="data.header.formatOptions"
                        :class="
                            _.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList
                        "
                    />
                    <template #edit="{ saving }">
                        <edit-date
                            v-model="data.header.editable.values[data.item.id]"
                            :options="data.header.formatOptions"
                            :loading="saving"
                            @click.native.stop
                        />
                    </template>
                </app-table-item-slot>
            </template>

            <!--
            Format: datetime
        -->
            <template v-for="slotDatetime in getSlotsByDisplayFormat('datetime')" #[slotDatetime]="data">
                <app-table-item-slot
                    :key="slotDatetime"
                    :value="data"
                    :show-default-value="isNil(data)"
                    :default-value="getTableRowFallback(data)"
                >
                    <display-datetime
                        :value="getTableRowValue(data)"
                        :options="data.header.formatOptions"
                        :class="
                            _.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList
                        "
                    />
                    <template #edit="{ saving }">
                        <edit-datetime
                            v-model="data.header.editable.values[data.item.id]"
                            :options="data.header.formatOptions"
                            :loading="saving"
                            @click.native.stop
                        />
                    </template>
                </app-table-item-slot>
            </template>

            <!--
            Format: percentage
        -->
            <template v-for="slotPercentage in getSlotsByDisplayFormat('percentage')" #[slotPercentage]="data">
                <app-table-item-slot
                    :key="slotPercentage"
                    :value="data"
                    :show-default-value="isNil(data)"
                    :default-value="getTableRowFallback(data)"
                >
                    <display-percentage
                        :value="getTableRowValue(data)"
                        :options="data.header.formatOptions"
                        :class="
                            _.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList
                        "
                    />
                    <template #edit="{ saving }">
                        <edit-percentage
                            v-model="data.header.editable.values[data.item.id]"
                            :options="data.header.formatOptions"
                            :loading="saving"
                            @click.native.stop
                        />
                    </template>
                </app-table-item-slot>
            </template>

            <!--
            Format: boolean
        -->
            <template v-for="slotBoolean in getSlotsByDisplayFormat('boolean')" #[slotBoolean]="data">
                <app-table-item-slot
                    :key="slotBoolean"
                    :value="data"
                    :show-default-value="isNil(data)"
                    :default-value="getTableRowFallback(data)"
                >
                    <display-boolean
                        :value="getTableRowValue(data)"
                        :options="data.header.formatOptions"
                        :class="
                            _.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList
                        "
                    />
                    <template #edit="{ saving }">
                        <edit-boolean
                            v-model="data.header.editable.values[data.item.id]"
                            :options="data.header.formatOptions"
                            :loading="saving"
                            @click.native.stop
                        />
                    </template>
                </app-table-item-slot>
            </template>

            <!--
            Format: wysiwyg
        -->
            <template v-for="slotWysiwyg in getSlotsByDisplayFormat('wysiwyg')" #[slotWysiwyg]="data">
                <app-table-item-slot
                    :key="slotWysiwyg"
                    :value="data"
                    :show-default-value="isNil(data)"
                    :default-value="getTableRowFallback(data)"
                >
                    <display-content
                        raw
                        :value="getTableRowValue(data)"
                        :options="data.header.formatOptions"
                        :class="
                            _.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList
                        "
                    />
                </app-table-item-slot>
            </template>

            <!--
            Format: enum
        -->
            <template v-for="slotEnum in getSlotsByDisplayFormat('enum')" #[slotEnum]="data">
                <app-table-item-slot
                    :key="slotEnum"
                    :value="data"
                    :show-default-value="isNil(data)"
                    :default-value="getTableRowFallback(data)"
                >
                    <display-enum
                        :value="getTableRowValue(data)"
                        :options="data.header.formatOptions"
                        :enums="data.header.enum"
                        :enum-a11y-key="data.header.enumA11yKey"
                        :enum-types="data.header.enumTypes"
                        :multiple="data.header.multiple"
                        :class="
                            _.isFunction(data.header.classList) ? data.header.classList(data) : data.header.classList
                        "
                    />
                    <template #edit="{ saving }">
                        <edit-enum
                            v-model="data.header.editable.values[data.item.id]"
                            :options="data.header.formatOptions"
                            :enums="data.header.enum"
                            :enum-a11y-key="data.header.enumA11yKey"
                            :loading="saving"
                            @click.native.stop
                        />
                    </template>
                </app-table-item-slot>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import getTableRowValue from '@/lib/helpers/getTableRowValue';
import getTableRowFallback from '@/lib/helpers/getTableRowFallback';
import BaseModel from '@/models/BaseModel';
import Collection from '@/models/Collection';
import resizeMixin from '@/mixins/resize';
import { createFilterQuery } from '@/store/modules/filterPanels';

export default {
    name: 'AppTable',
    components: {
        AppTableLoading: () => import(/* webpackChunkName: "core" */ './Loading'),
        AppTableNoData: () => import(/* webpackChunkName: "core" */ './NoData'),
        AppTableHeaderDataTableSelect: () => import(/* webpackChunkName: "core" */ './HeaderDataTableSelect'),
        AppTableItemDataTableSelect: () => import(/* webpackChunkName: "core" */ './ItemDataTableSelect'),
        AppTableFooter: () => import(/* webpackChunkName: "core" */ './Footer'),
        AppTableHeader: () => import(/* webpackChunkName: "core" */ './Header'),
        AppTableTop: () => import(/* webpackChunkName: "core" */ './Top'),
        AppTableItemSlot: () => import(/* webpackChunkName: "core" */ './ItemSlot')
    },
    mixins: [resizeMixin],
    props: {
        /**
         * Selected items when multi-select enabled
         */
        value: {
            type: Array,
            default: () => []
        },
        /**
         * An array of header objects
         */
        headers: {
            type: Array,
            default: () => []
        },
        /**
         * An array of items
         */
        items: {
            type: Array,
            default: () => []
        },
        /**
         * An array of header keys
         */
        defaultHeaders: {
            type: Array,
            default: () => []
        },
        /**
         * An array of header keys
         */
        exportHeaders: {
            type: Array,
            default: () => []
        },
        /**
         * Whether to display a custom header
         */
        hideDefaultHeader: {
            type: Boolean,
            default: true
        },
        /**
         * An array of preset filters
         */
        presetFilters: {
            type: Object,
            default: () => {}
        },
        /**
         * Asynchronous function to get the items
         * Replaces the items prop
         */
        requestItems: {
            type: Function,
            default: null
        },
        /**
         * Asynchronous function to get the filtered items
         */
        filterRequestItems: {
            type: Function,
            default: null
        },
        /**
         * Whether to give the table an outline
         */
        outlined: {
            type: Boolean,
            default: false
        },
        /**
         * Whether to make the table dense
         */
        dense: {
            type: Boolean,
            default: true
        },
        /**
         * Whether the table is in a loading state
         */
        loading: {
            type: Boolean,
            default: false
        },
        /**
         * Specify the height of the table
         */
        height: {
            type: String,
            default: '100%'
        },
        /**
         * Specify the max height of the table
         */
        maxHeight: {
            type: String,
            default: 'auto'
        },
        /**
         * Specify whether the table has checkboxes and how many can be selected
         */
        selectable: {
            type: [Boolean, Number, Function],
            default: false
        },
        /**
         * Specify whether the table has clickable rows
         */
        clickable: {
            type: Boolean,
            default: false
        },
        /**
         * Specify whether the table rows are expandable
         */
        expandable: {
            type: Boolean,
            default: false
        },
        /**
         * Specify whether to display export buttons
         */
        exportable: {
            type: [Boolean, Array, Object],
            default: false
        },
        /**
         * Specify whether the table is sortable
         */
        sortable: {
            type: Boolean,
            default: true
        },
        /**
         * Specify whether the table has a fixed header
         */
        fixedHeader: {
            type: Boolean,
            default: true
        },
        /**
         * Specify whether to display the table header
         */
        hideHeader: {
            type: Boolean,
            default: false
        },
        /**
         * Specify whether to display the table header
         */
        hideTop: {
            type: Boolean,
            default: false
        },
        /**
         * Specify whether to display the table footer
         */
        hideFooter: {
            type: Boolean,
            default: false
        },
        /**
         * Specify how to display the table rows
         */
        displayFormat: {
            type: String,
            default: 'table'
        },
        /**
         * Specify the display formats available
         */
        displayFormats: {
            type: Array,
            default: () => []
        },
        /**
         * Should the expanded item be sticky or scroll with the parent?
         */
        stickyExpandedItem: {
            type: Boolean,
            default: false
        },
        /**
         * Specify whether to lock the display format
         */
        lockDisplay: {
            type: Boolean,
            default: false
        },
        /**
         * Specify whether to display the "no-data" message
         */
        showLacuna: {
            type: Boolean,
            default: false
        },
        /**
         * Specify what to cast each row item to
         */
        cast: {
            type: Function,
            default: null
        },
        /**
         * Specify the header of which the rows will be split into columns
         */
        kanbanGroup: {
            type: String,
            default: null
        },
        /**
         * Specify the kanban columns
         */
        kanbanColumns: {
            type: Array,
            default: null
        },
        /**
         * Specify whether to display the option to show/hide columns
         */
        hideColumnVisibility: {
            type: Boolean,
            default: false
        },
        /**
         * Specify whether to display the filter button
         */
        hideFilters: {
            type: Boolean,
            default: false
        },
        /**
         * Specify whether to hide all top actions
         */
        hideActions: {
            type: Boolean,
            default: false
        },
        /**
         * Specify whether to enable lazy loading (disables default pagination)
         */
        lazyLoad: {
            type: Boolean,
            default: false
        },
        /**
         * Show quick filters beneath the table header
         */
        showQuickFilters: {
            type: Boolean,
            default: false
        },
        /**
         * Displays validation error when no rows selected
         */
        required: {
            type: Boolean,
            default: false
        },
        /**
         * Set the table-layout CSS property
         */
        tableLayout: {
            type: String,
            default: null
        },
        /**
         * Message to display when there are no results
         */
        noDataMessage: {
            type: String,
            default: null
        }
    },
    data: () => ({
        getTableRowValue,
        getTableRowFallback,
        getItemsDebounced: () => {},
        isLoading: true,
        isMobile: false,
        drag: {
            startX: 0,
            startY: 0,
            endX: 0,
            endY: 0
        },
        $tableWrapper: null,
        activeHeaderKeys: null,
        display: null,
        expandedItemWidth: null,
        kanbanData: {},
        errorMessages: [],
        options: {
            page: 1,
            itemsPerPage: 100,
            sortBy: [],
            sortDesc: [],
            groupBy: [],
            groupDesc: [],
            multiSort: false,
            mustSort: false
        },
        params: {
            offset: 0,
            limit: 100,
            sort: [],
            filters: {}
        },
        response: {
            data: [],
            metadata: {
                total: -1,
                limit: 0,
                offset: 0
            }
        },
        itemSlots: [],
        filterPanelId: 'table',
        filtersLoaded: false
    }),
    computed: {
        id() {
            return `table-${this._uid}`;
        },
        selected: {
            get() {
                return this.value;
            },
            set(value) {
                this.validate(value);
                this.$emit('input', value);
            }
        },
        someItemsSelected() {
            return this.selected.length > 0;
        },
        everyItemSelected() {
            if (this.selected.length === 0) {
                return false;
            }

            if (typeof this.selectable === 'function') {
                const selectable = this.rows.filter(this.selectable);
                return selectable.length === this.selected.length;
            }

            return this.selected.length === this.rows.length;
        },
        visibleHeaders() {
            const headers = this.headers.filter(h => !('active' in h) || h.active === true);

            if (!this.activeHeaderKeys || this.display === 'cards' || this.display === 'kanban') {
                return headers;
            }

            return headers.filter(h => this.activeHeaderKeys.includes(h.column));
        },
        filterableHeaders() {
            return this.headers.filter(h => h.filterable);
        },
        expandedItemColspan() {
            return this.visibleHeaders.length + 1;
        },
        hasPresetItems() {
            return !this._.isFunction(this.requestItems);
        },
        rows() {
            let rows = [];

            if (this.hasPresetItems) {
                rows = this.items;
            } else {
                rows = this.response.data;
            }

            rows = rows.map((row, index) => ({ id: index, ...row }));

            if (this.cast) {
                return rows.map(r => (r instanceof BaseModel ? r : new this.cast(r)));
            }

            return rows;
        },
        itemsLength() {
            return this.hasPresetItems ? -1 : this.response.metadata.total;
        },
        showFooter() {
            if (this.hideFooter) {
                return false;
            }

            if (this.showLacuna) {
                return false;
            }

            if (this.hasPresetItems) {
                return false;
            }

            if (this.response.metadata.total > 0) {
                return true;
            }

            return false;
        },
        showCardSlot() {
            return this.findSlot('card') && this.display === 'cards';
        },
        tableClassList() {
            let classList = ['app-table', 'app-table-data'];

            if (this.outlined) {
                classList.push('app-table--outlined');
            }

            if (this.selectable) {
                classList.push('app-table--selectable');
            }

            if (this.clickable) {
                classList.push('app-table--clickable');
            }

            if (this.exportable) {
                classList.push('app-table--exportable');
            }

            if (this.expandable) {
                classList.push('app-table--expandable');
            }

            if (this.fixedHeader) {
                classList.push('app-table--fixed-header');
            }

            if (!this.hideTop) {
                classList.push('app-table--with-header');
            }

            if (!this.response.metadata.total) {
                classList.push('app-table--empty');
            }

            if (this.showLacuna) {
                classList.push('app-table--lacuna');
            }

            if (this.showQuickFilters) {
                classList.push('app-table--quick-filters');
            }

            if (this.loading || this.isLoading) {
                classList.push('app-table--loading');
            }

            if (this.tableLayout) {
                classList.push(`app-table--layout--${this.tableLayout}`);
            }

            classList.push(`app-table--${this.display}`);

            return classList.join(' ');
        },
        showDefaultHeader() {
            if (this.isMobile || this.display === 'cards') {
                return true;
            }

            return !this.hideDefaultHeader;
        },
        hideHeaderBar() {
            return this.hideHeader || this.display === 'kanban';
        },
        hideProgress() {
            return this.display === 'kanban';
        },
        formats() {
            if (this.displayFormats.length) {
                return this.displayFormats;
            }

            const formats = ['table', 'cards'];

            if (this.kanbanGroup && this.kanbanColumns) {
                formats.push('kanban');
            }

            return formats;
        },
        maxSelectable() {
            return Number.isInteger(this.selectable) ? Number(this.selectable) : null;
        },
        preventClick() {
            const distanceX = Math.abs(this.drag.endX - this.drag.startX);
            const distanceY = Math.abs(this.drag.endY - this.drag.startY);

            return distanceX > 5 || distanceY > 5;
        }
    },
    watch: {
        display(format) {
            this.$emit('display-format', format);
        },
        displayFormat(format) {
            this.display = format;
        },
        'params.filters'(filters = {}) {
            this.$store.dispatch('filterPanels/removeFilters', { panelId: this.filterPanelId });

            let filtersArray = [];

            if (Object.keys(filters).length) {
                for (let [column, filterString] of Object.entries(filters)) {
                    const filter = this.$store.getters['filterPanels/filterFromFilterString'](
                        this.filterPanelId,
                        column,
                        filterString
                    );

                    if (filter) {
                        filtersArray.push(filter);
                    }
                }
            }

            if (filtersArray.length) {
                this.$store.dispatch('filterPanels/setFilters', {
                    panelId: this.filterPanelId,
                    filters: filtersArray
                });
            }
        },
        filtersLoaded(v) {
            if (v) {
                this.init();
            }
        },
        items(items) {
            this.setKanbanData();

            if (Array.isArray(items)) {
                this.options.itemsPerPage = items.length ? items.length : 100;
                this.isLoading = false;
            }
        }
    },
    beforeDestroy() {
        this.$store.dispatch('filterPanels/remove', this.filterPanelId);
    },
    async mounted() {
        this.filterPanelId = this.uuid;

        if (this.lockDisplay) {
            this.display = this.displayFormat;
        } else if (this.formats.includes(this.displayFormat)) {
            this.display = this.displayFormat;
        } else if (this.formats.includes(this.$store.getters['preferences/table_display_format'])) {
            this.display = this.$store.getters['preferences/table_display_format'];
        } else {
            this.display = this.formats[0];
        }

        this.getItemsDebounced = this._.debounce(this.getItems, 100);

        await this.init();
    },
    methods: {
        async init(options = {}) {
            if (
                this.filterableHeaders.length &&
                !this.hideFilters &&
                !this.hideTop &&
                !this.hideActions &&
                !this.filtersLoaded
            ) {
                return;
            }

            options = {
                resetHeaders: 'resetHeaders' in options ? options.resetHeaders : true,
                resetFilters: 'resetFilters' in options ? options.resetFilters : true
            };

            this.setItemSlots();

            await this.$nextTick();
            this.reset(options);

            await this.$nextTick();
            await this.updateOptions();

            if (this._.has(this.$refs[this.id], '$el')) {
                this.$tableWrapper = this.$refs[this.id].$el.querySelector('.v-data-table__wrapper');
            }

            this.setExpandedItemWidth();
            this.$emit('load');
        },
        setItemSlots() {
            let slots = [];

            if (!this.showCardSlot) {
                slots = this._.keys(
                    this._.pickBy(this.$scopedSlots, (v, k) => {
                        return k.startsWith('item.') && k !== 'item.control';
                    })
                );
            }

            this.itemSlots = slots;
        },
        async reset(args = { resetHeaders: true, resetFilters: true }) {
            args = {
                resetHeaders: 'resetHeaders' in args ? args.resetHeaders : true,
                resetFilters: 'resetFilters' in args ? args.resetFilters : true
            };

            //

            const tableOptions = {
                page: 1,
                itemsPerPage: 100,
                sortBy: [],
                sortDesc: [],
                groupBy: [],
                groupDesc: [],
                multiSort: false,
                mustSort: false
            };

            this.options = tableOptions;

            //

            const tableParams = {
                offset: 0,
                limit: 100,
                sort: [],
                sorting: false,
                filters: {}
            };

            if (args.resetFilters) {
                if (this.presetFilters) {
                    tableParams.filters = { ...this.presetFilters };
                }
            } else {
                tableParams.filters = this.params.filters;
            }

            this.params = tableParams;

            //

            this.response = {
                data: [],
                metadata: {
                    total: -1,
                    limit: 0,
                    offset: 0
                }
            };

            if (args.resetHeaders) {
                this.resetHeaders();
            }

            if (Array.isArray(this.items)) {
                this.options.itemsPerPage = this.items.length ? this.items.length : 100;
            }
        },
        resetHeaders() {
            if (!this.defaultHeaders || !this.defaultHeaders.length) {
                this.activeHeaderKeys = this.headers.map(h => h.column);
            } else {
                this.activeHeaderKeys = this.headers
                    .filter(header => header.persistent || this.defaultHeaders.includes(header.column))
                    .map(h => h.column);
            }
        },
        async updateOptions(options = {}) {
            if (
                (options.sortBy || options.sortDesc) &&
                (options.sortBy !== this.options.sortBy || options.sortDesc !== this.options.sortDesc)
            ) {
                this.params.sorting = true;
                this.$refs[this.id].expansion = {};
            } else {
                this.params.sorting = false;
            }

            if (this._.has(options, 'page')) {
                this.options.page = options.page;
            }

            if (this._.has(options, 'itemsPerPage')) {
                this.options.itemsPerPage = options.itemsPerPage;
            }

            if (this._.has(options, 'sortBy')) {
                this.options.sortBy = options.sortBy;
            }

            if (this._.has(options, 'sortDesc')) {
                this.options.sortDesc = options.sortDesc;
            }

            const offset = this.options.page * this.options.itemsPerPage - this.options.itemsPerPage;
            if (offset !== this.params.offset) {
                this.$emit('update:offset', offset);
            }
            this.params.offset = offset;

            const limit = this.options.itemsPerPage;
            if (limit !== this.params.limit) {
                this.$emit('update:limit', limit);
            }
            this.params.limit = limit;

            const sort = this.options.sortBy.map((sort, index) => {
                const key = this.headers.find(h => h.value === sort).sortKey;
                return key + ':' + (this.options.sortDesc[`${index}`] ? 'desc' : 'asc');
            });
            if (sort.join(',') !== this.params.sort.join(',')) {
                this.$emit('update:sort', sort);
            }
            this.params.sort = sort;

            this.getItemsDebounced();
        },
        async getItems() {
            if (this.hasPresetItems || !this.requestItems) {
                this.setKanbanData();
                this.isLoading = false;
                return Promise.resolve();
            }

            this.isLoading = true;

            try {
                let { offset, limit, sort, sorting, filters } = this.params;
                let preSortLimit = limit;

                if (sorting) {
                    limit = this.response.data.length || offset + limit;
                    offset = 0;
                }

                let response = await this.requestItems(offset, limit, sort, { ...filters });

                response = this._.clone(response);

                if (response === null || response === undefined) {
                    return false;
                }

                if (!this._.has(response, 'metadata.total')) {
                    if (this._.has(response, 'data') && Array.isArray(response.data)) {
                        response.metadata = {
                            total: response.data.length,
                            limit: response.data.length,
                            offset: 0
                        };
                    } else {
                        throw new Error(this.$t('response_not_recognised'));
                    }
                }

                if (this.lazyLoad) {
                    if (sorting) {
                        this.response = response;
                        this.options.itemsPerPage = preSortLimit;
                    } else {
                        const newItemIds = response.data.map(item => item.id);
                        this.response.data = this.response.data.filter(item => {
                            return !newItemIds.includes(item.id);
                        });
                        this.response.data.push(...response.data);
                        this.response.metadata = response.metadata;
                    }

                    try {
                        this.options.page =
                            parseInt(Number(this.response.data.length / this.options.itemsPerPage).toFixed(0), 10) || 1;
                    } catch (error) {
                        this.options.page = 1;
                    }

                    if (this.$tableWrapper) {
                        await this.$nextTick();
                        this.$tableWrapper.scrollTop = this.$tableWrapper.scrollTopMax;
                    }
                } else {
                    this.response = response;

                    if (this.$tableWrapper) {
                        await this.$nextTick();
                        this.$tableWrapper.scrollTop = 0;
                    }
                }

                this.setKanbanData();

                this.$emit('response', { ...this.response });
            } catch (error) {
                this.$emit('error', error);
                // this.handleError(error, () => this.getItems(), false);
            }

            this.isLoading = false;
        },
        onSort(columnKey) {
            this.$emit('sort', columnKey);
        },
        async onApplyFilters(filters) {
            this.reset({ resetHeaders: false });

            this.params.filters = {
                ...createFilterQuery(filters)
            };

            this.getItemsDebounced();
            this.$emit('apply-filters', { filters, query: this.params.filters });
        },
        async onClearFilters() {
            this.reset();

            this.params.filters = {};

            this.getItemsDebounced();
            this.$emit('clear-filters');
        },
        getSlotsByDisplayFormat(format) {
            if (this.showCardSlot) {
                return [];
            }
            return this.headers
                .filter(h => h.display === format)
                .map(h => `item.${h.value}`)
                .filter(s => !this.itemSlots.includes(s));
        },
        isNil(data) {
            let value = this.getTableRowValue(data);

            if (!this._.has(data.header, 'transform')) {
                if (this._.get(data, 'header.format') === 'money') {
                    value = value && 'amount' in value ? value.amount : null;
                    return !value || !this._.isNumber(value);
                } else if (this._.get(data, 'header.format') === 'number') {
                    return !this._.isNumber(value);
                } else if (this._.get(data, 'header.format') === 'percentage') {
                    return !this._.isNumber(value);
                }
            }

            return value === null || value === undefined;
        },
        setExpandedItemWidth() {
            if (this.stickyExpandedItem) {
                this.expandedItemWidth = this.getWidth(this.findElement('.v-data-table__wrapper')) - 8;
            }
        },
        onWindowResize() {
            this.setExpandedItemWidth();
        },
        insertResponseDataItem(newItem) {
            this.response.data = [newItem].concat(this.response.data);
            this.response.metadata.total++;
        },
        updateResponseData(data = []) {
            for (const item of data) {
                if (this.response.data.find(row => row.id === item.id)) {
                    this.response.data = this.response.data.map(row => {
                        if (row.id === item.id) {
                            row = item;
                        }
                        return row;
                    });
                }
            }

            this.setKanbanData();
        },
        updateResponseDataItem(id, newItem) {
            if (this.response.data.find(row => row.id === id)) {
                this.response.data = this.response.data.map(row => {
                    if (row.id === id) {
                        row = newItem;
                    }
                    return row;
                });
            } else {
                this.insertResponseDataItem(newItem);
            }

            this.setKanbanData();
        },
        async removeResponseDataItem(id) {
            this.response.data = this.response.data.filter(row => {
                return row.id !== id;
            });

            if (this.response.metadata.total <= this.response.data.length) {
                this.response.metadata.total = this.response.data.length;
            } else {
                this.response.metadata.total = this.response.metadata.total - 1;
            }

            await Collection.removeItemInCollections(id);

            this.setKanbanData();
        },
        setKanbanData() {
            if (!this.kanbanGroup || !this.kanbanColumns) {
                return;
            }

            const data = this.kanbanColumns.reduce((acc, column) => {
                if (typeof column === 'object') {
                    acc[`${column.id}`] = [];
                } else {
                    acc[`${column}`] = [];
                }
                return acc;
            }, {});

            for (const row of this.rows) {
                const columnValue = row[`${this.kanbanGroup}`];

                for (const kanbanColumn of this.kanbanColumns) {
                    if (typeof kanbanColumn === 'object') {
                        if ('acceptor' in kanbanColumn && typeof kanbanColumn.acceptor === 'function') {
                            if (kanbanColumn.acceptor(columnValue)) {
                                data[`${kanbanColumn.id}`].push(row);
                            }
                        } else if (columnValue === kanbanColumn.id) {
                            data[`${kanbanColumn.id}`].push(row);
                        }
                    } else {
                        if (columnValue === kanbanColumn) {
                            data[`${kanbanColumn}`].push(row);
                        }
                    }
                }
            }

            this.kanbanData = data;

            return data;
        },
        onMouseDown(event) {
            this.drag.endX = 0;
            this.drag.endY = 0;

            this.drag.startX = event.clientX;
            this.drag.startY = event.clientY;
        },
        onMouseUp(event) {
            this.drag.endX = event.clientX;
            this.drag.endY = event.clientY;
        },
        onClickRow(item) {
            if (this.preventClick || !this.clickable) {
                return;
            }

            this.$emit('click:row', item);

            let selectable = false;

            if (typeof this.selectable === 'function') {
                selectable = this.selectable(item);
            } else {
                selectable = this.selectable;
            }

            if (selectable) {
                if (this.selected.find(sel => sel.id === item.id)) {
                    this.selected = this.selected.filter(sel => sel.id !== item.id);
                } else if (this.maxSelectable !== null && this.maxSelectable === 1) {
                    this.selected = [item];
                } else if (this.maxSelectable === null || this.selected.length < this.maxSelectable) {
                    this.selected.push(item);
                }
            }
        },
        selectAllToggle({ items, value }) {
            if (value) {
                if (typeof this.selectable === 'function') {
                    items = items.filter(item => this.selectable(item));
                }

                this.selected = items;
            } else {
                this.selected = [];
            }
        },
        validate(selected = null) {
            selected = Array.isArray(selected) && selected ? selected : this.selected;

            this.errorMessages = [];

            if (!this.required) {
                return true;
            }

            if (!this.selectable) {
                return true;
            }

            if (Array.isArray(selected) && selected.length) {
                return true;
            }

            if (this.maxSelectable === 1) {
                this.errorMessages.push(this.$t('you_must_select_one_item'));
                return this.$t('you_must_select_one_item');
            }

            this.errorMessages.push(this.$t('you_must_select_at_least_one_item'));
            return this.$t('you_must_select_at_least_one_item');
        },
        setTableRowValue(data, value) {
            data.value = value;
            data.item[data.header.value] = value;

            if (data.header.editable && 'values' in data.header.editable && data.header.editable.values) {
                data.header.editable.values[data.item.id] = value;
            }
        }
    }
};
</script>
